import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import InfoPage from "../InfoPage";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import MembershipBadge from '../../components/MembershipBadge';
import FounderBadge from '../../components/FounderBadge';
import CollabBadge, { CollabTrustStats, StampMark } from '../../components/CollabBadge';
import ModalPortal from '../../components/Modal/ModalPortal';
import Dropdown from '../../components/Dropdown';
import { getCloudinaryUrl } from '../../utils/cloudinary';
import './Hosts.sass';
import { FavoriteInline, FavoriteFilterButton } from '../../components/FavoriteToggle';
import { toggleFavorite } from '../../utils/favorites';
import { getAuthToken } from '../../utils/authHelpers';
import IntroVideoPreview from '../../components/IntroVideoPreview';
import { showToast } from '../../components/Messaging/Toast';

// Same allowlist as the collab board share pill — team members who can copy
// public share links.
const SHARE_ALLOWLIST = ['jamie@postaway.com', 'jonathansgallegos@gmail.com', 'jtkraus@yahoo.com'];

// Lazy-loaded so the heavy mapbox-gl bundle only downloads when a user opens
// the Map view.
const CollabBoardMap = lazy(() => import('../../components/CollabBoardMap'));
const MAP_ENABLED = Boolean(process.env.REACT_APP_MAPBOX_TOKEN);

// Modal component for showing property details
const PropertyModal = ({ isOpen, onClose, property, onFavoriteToggle, membershipTier }) => {
  const [isPortrait, setIsPortrait] = useState(null);
  const [useRawImage, setUseRawImage] = useState(false);

  // Reset state and detect image dimensions when modal opens or image changes
  useEffect(() => {
    setIsPortrait(null);
    setUseRawImage(false);
    
    // Preload image to detect aspect ratio (handles cached images)
    if (property?.image) {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) {
          setIsPortrait(img.naturalWidth < img.naturalHeight);
        }
      };
      img.onerror = () => {
        setIsPortrait(false);
      };
      img.src = property.image;
      
      // Cleanup function to prevent memory leaks
      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }
  }, [isOpen, property?.image]);

  // Get logged-in user's type from localStorage
  const loggedInUserType = typeof window !== 'undefined' 
    ? window.localStorage.getItem('postaway_user_type') || '' 
    : '';
  
  // Hide Message Host button for travelers
  const canMessage = loggedInUserType !== 'traveler';

  if (!property) return null;

  // Handle image load to detect aspect ratio
  const handleImageLoad = (e) => {
    const img = e.target;
    if (img.naturalWidth && img.naturalHeight) {
      setIsPortrait(img.naturalWidth < img.naturalHeight);
    }
  };

  const shouldShowBlur = isPortrait === true;
  
  // Optimize modal images with Cloudinary (larger size for modal view)
  const optimizedModalImage = property.image ? getCloudinaryUrl(property.image, {
    width: 1200,
    height: 675,
    crop: 'fill',
    quality: 'auto',
    format: 'auto'
  }) : property.image;
  const modalImageSrc = useRawImage ? property.image : optimizedModalImage;

  // Cloudinary fetch rejects sources over 10MB, so retry with the original URL
  // before giving up and defaulting to landscape behavior.
  const handleImageError = () => {
    if (!useRawImage && optimizedModalImage && optimizedModalImage !== property.image) {
      setUseRawImage(true);
      return;
    }
    setIsPortrait(false);
  };

  // Public share link (/p/:slugOrId) for allowlisted team members.
  const viewerEmail = (() => {
    try { return (window.localStorage.getItem('postaway_email') || '').toLowerCase(); } catch (_) { return ''; }
  })();
  const canSharePublicLink = SHARE_ALLOWLIST.includes(viewerEmail) && !!property.property_id;
  const handleCopyPublicLink = async () => {
    const slugOrId = property.property_slug || property.slug || property.property_id;
    const url = `${window.location.origin}/p/${encodeURIComponent(slugOrId)}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      showToast('Public property link copied', 'success', null, 3000);
    } catch (_) {
      showToast('Unable to copy link. Please try again.', 'error', null, 4000);
    }
  };

  return (
    <ModalPortal isOpen={isOpen} onClose={onClose} maxWidth="600px">
        {/* 16:9 Header Image with Blurred Side Panels for Portrait Images */}
        <div className="property-modal-header" style={{ 
          justifyContent: shouldShowBlur ? 'center' : 'stretch'
        }}>
          {/* Blurred background layer - only for portrait images */}
          {shouldShowBlur && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${modalImageSrc})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'blur(20px)',
              transform: 'scale(1.1)',
              zIndex: 0
            }} />
          )}
          
          {/* Main image */}
          <img 
            key={property.image}
            src={modalImageSrc} 
            alt={property.title}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ 
              position: 'relative',
              zIndex: 1,
              width: shouldShowBlur ? 'auto' : '100%',
              height: shouldShowBlur ? '100%' : '100%',
              maxWidth: shouldShowBlur ? '100%' : 'none',
              maxHeight: shouldShowBlur ? '100%' : 'none',
              objectFit: shouldShowBlur ? 'contain' : 'cover'
            }}
          />
        </div>
        
        <div className="property-modal-content">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2 className="property-modal-title">
              {property.title}
              <FavoriteInline
                isFavorited={property.is_favorited || false}
                onClick={() => onFavoriteToggle && onFavoriteToggle(property.property_id)}
                size={18}
              />
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '100%' }}>
              <CollabTrustStats
                completedCollabs={property.completed_collabs}
                averageRating={property.host_average_rating}
                totalReviews={property.host_total_reviews}
                compact
                reviewsHref={property.host_profile_id
                  ? `/profile?host_id=${encodeURIComponent(property.host_profile_id)}${property.property_id ? `&property_id=${encodeURIComponent(property.property_id)}` : ''}#reviews`
                  : null}
              />
              <FounderBadge isFounder={property.founder} />
              <MembershipBadge planId={property.plan_id} planName={property.plan_name} userType="host" />
              {canSharePublicLink && (
                <button
                  type="button"
                  className="copy-link-pill"
                  aria-label="Copy public property link"
                  title="Copy public property link"
                  onClick={handleCopyPublicLink}
                >
                  <svg
                    className="copy-link-pill__icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                  <span className="copy-link-pill__label">Share</span>
                </button>
              )}
            </div>
          </div>

          {property.location && (
            <p className="property-modal-location">{property.location}</p>
          )}
          
          <div className="property-modal-badges">
            {property.bedrooms && (
              <span>
                {property.bedrooms} {property.bedrooms === 1 ? 'Bedroom' : 'Bedrooms'}
              </span>
            )}
            {property.bathrooms != null && (
              <span>
                {Number(property.bathrooms)} {Number(property.bathrooms) === 1 ? 'Bathroom' : 'Bathrooms'}
              </span>
            )}
            {property.property_type && (
              <span>{property.property_type}</span>
            )}
          </div>
          
          <div className="property-modal-actions">
            <div className="property-modal-buttons">
              {property.host_instagram && membershipTier !== 'basic' && (
                <a 
                  className="button-link"
                  href={property.host_instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.405a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z"/>
                  </svg>
                  Instagram
                </a>
              )}
              {property.host_website && membershipTier !== 'basic' && (
                <a 
                  className="button-link"
                  href={property.host_website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Website
                </a>
              )}
              {/* View Profile button */}
              {property.host_profile_id && (
                <button
                  className="button-profile"
                  onClick={() => { 
                    const params = new URLSearchParams();
                    if (property.host_profile_id) params.set('host_id', property.host_profile_id);
                    if (property.property_id) params.set('property_id', property.property_id);
                    window.location.href = `/profile?${params.toString()}`;
                  }}
                >
                  View Profile
                </button>
              )}
              {/* Message Host button */}
              {property.user_id && canMessage && (
                <button
                  className="button-message"
                  onClick={() => { 
                    const params = new URLSearchParams();
                    params.set('to', property.user_id);
                    if (property.title) params.set('title', property.title);
                    window.location.href = `/messages?${params.toString()}`;
                  }}
                >
                  Message Host
                </button>
              )}
              {/* View Collab Post button - only show if host has active collab post */}
              {property.has_active_collab_post && property.property_id && property.membership_tier !== 'basic' && (
                <button
                  className="button-profile button-collab-post"
                  onClick={() => { window.location.href = `/collab-board?propertyId=${encodeURIComponent(property.property_id)}`; }}
                >
                  View Collab Post
                </button>
              )}
              {property.membership_tier === 'basic' && (
                <Tippy content="This host is not currently eligible for collaboration on Postaway" placement="top">
                  <span>
                    <button
                      className="button-profile button-collab-post"
                      disabled
                      style={{ opacity: 0.5, cursor: 'not-allowed' }}
                    >
                      Request Collab
                    </button>
                  </span>
                </Tippy>
              )}
              {/* View Travel Post button - only show if host has active travel post */}
              {property.active_travel_post_id && (
                <button
                  className="button-profile"
                  onClick={() => { window.location.href = `/travel-board?travelPostId=${encodeURIComponent(property.active_travel_post_id)}`; }}
                >
                  View Travel Post
                </button>
              )}
            </div>
            
            {/* Booking Links - Icons on the right */}
            <div className="property-modal-booking-links">
              {property.direct_booking_url && (
                <Tippy content="Book directly with the host and save" zIndex={100000} appendTo={() => document.body}>
                <a
                  href={property.direct_booking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <img src="/circle-p-logo.png" alt="Direct" />
                </a>
                </Tippy>
              )}
              {property.vrbo_url && (
                <a
                  href={property.vrbo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <img src="/vrbo logo.png" alt="VRBO" />
                </a>
              )}
              {property.airbnb_url && (
                <a
                  href={property.airbnb_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <img src="/airbnb_logo.png" alt="Airbnb" />
                </a>
              )}
            </div>
          </div>
        </div>
    </ModalPortal>
  );
};

// Individual Host Property Card Component
const HostCard = ({ host, onClick, onFavoriteToggle }) => {
  const isBasicHost = host.membership_tier === 'basic';
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);
  const imgRef = useRef(null);

  // Resolve image URL based on environment
  const resolveImageUrl = (rawUrl) => {
    const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    let resolved = rawUrl || '';
    
    if (resolved.startsWith('/')) {
      if (isLocal) {
        resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
      }
    }
    
    return resolved || null;
  };

  const rawImageUrl = resolveImageUrl(host.property_image_url);
  
  const optimizedImageUrl = rawImageUrl ? getCloudinaryUrl(rawImageUrl, {
    width: 450,
    height: 600,
    crop: 'fill',
    quality: 'auto',
    format: 'auto'
  }) : null;

  useEffect(() => {
    if (optimizedImageUrl && optimizedImageUrl !== rawImageUrl) {
      setCurrentImageUrl(optimizedImageUrl);
    } else if (rawImageUrl) {
      setCurrentImageUrl(rawImageUrl);
    }
  }, [optimizedImageUrl, rawImageUrl]);

  const handleImageError = () => {
    if (!hasTriedFallback && optimizedImageUrl && optimizedImageUrl !== rawImageUrl && currentImageUrl === optimizedImageUrl) {
      setHasTriedFallback(true);
      setCurrentImageUrl(rawImageUrl);
      setIsLoaded(false);
      setIsInView(true);
      return;
    }
    setImageFailed(true);
  };

  // Intersection Observer for lazy loading - must be called before early return
  useEffect(() => {
    if (!imgRef.current || !currentImageUrl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
      }
    );

    observer.observe(imgRef.current);

    return () => {
      observer.disconnect();
    };
  }, [currentImageUrl]);
  
  // Don't render if no image or both URLs failed
  if (!currentImageUrl || imageFailed) return null;
  
  // Display property handle from property slug (fallback to property name)
  const slug = host.property_slug || host.slug;
  const nameFallback = host.property_name || host.name || 
    (() => {
      const parts = [];
      if (host.location_city) parts.push(host.location_city);
      if (host.location_state) parts.push(host.location_state);
      if (host.location_country && host.location_country !== 'United States') {
        parts.push(host.location_country);
      }
      return parts.join(', ') || 'Property';
    })();
  const displayName = slug ? `@${slug}` : nameFallback;

  const cardContent = (
    <div 
      className={`host-card${isBasicHost ? ' host-card-basic' : ''}`}
      onClick={() => onClick(host, currentImageUrl)}
    >
      <div className={`host-image-wrapper${host.has_new_activity ? ' has-activity' : ''}`} ref={imgRef}>
        {(() => {
          const cardVideoUrl = host.property_intro_video_url || host.intro_video_url;
          const cardVideoPoster = host.property_intro_video_poster_url || host.intro_video_poster_url;
          const cardVideoPublicId = host.property_intro_video_public_id || host.intro_video_public_id;
          if (isInView && cardVideoUrl) {
            return (
              <IntroVideoPreview
                mode="card"
                posterUrl={cardVideoPoster || currentImageUrl}
                publicId={cardVideoPublicId}
                fullUrl={cardVideoUrl}
                fallbackImageUrl={currentImageUrl}
                alt={displayName}
              />
            );
          }
          return null;
        })()}
        {!(host.property_intro_video_url || host.intro_video_url) && isInView && (
          <img
            src={currentImageUrl}
            alt={displayName}
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            onLoad={() => setIsLoaded(true)}
            onError={handleImageError}
            className={isLoaded ? 'loaded' : ''}
          />
        )}
        <CollabBadge
          completedCollabs={host.completed_collabs}
          isFounder={host.founder}
          ratingDistribution={host.host_rating_distribution}
          variant="stamp-avatar"
        />
      </div>
      <div className="host-name">
        <span>{displayName}</span>
        <FavoriteInline
          isFavorited={host.is_favorited || false}
          onClick={() => onFavoriteToggle && onFavoriteToggle(host.property_id)}
          size={16}
        />
      </div>
    </div>
  );

  // Wrap with Tippy tooltip if host has active collab/travel post
  if (host.has_new_activity) {
    return (
      <Tippy content="Member has an active post on the Collab or Travel board" placement="top">
        {cardContent}
      </Tippy>
    );
  }

  return cardContent;
};

const Hosts = () => {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [membershipTier, setMembershipTier] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [selectedHost, setSelectedHost] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [randomSeed, setRandomSeed] = useState(null);
  const [sortBy, setSortBy] = useState('Random');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [collabEligibleOnly, setCollabEligibleOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  // Grid vs Map view (map gated on a configured Mapbox token), shareable via ?view=map.
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState(() =>
    (MAP_ENABLED && searchParams.get('view') === 'map') ? 'map' : 'grid'
  );
  const [mapHosts, setMapHosts] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const mapFetchedRef = useRef(false);
  
  const sortOptions = [
    'Random',
    'Alphabetical',
    'Newest to Oldest',
    'Oldest to Newest',
    'Recent Activity',
    'Trending'
  ];

  // Fetch hosts data
  const fetchHosts = async (pageNum, search = '', sort = sortBy) => {
    setLoading(true);
    try {
      // Use existing seed for subsequent pages, null for new searches or sort changes
      const seedToUse = (pageNum === 1 && (search !== searchTerm || sort !== sortBy)) ? null : randomSeed;
      
      // Map sort option to API sort value
      const sortMap = {
        'Random': 'random',
        'Alphabetical': 'alphabetical',
        'Newest to Oldest': 'newest',
        'Oldest to Newest': 'oldest',
        'Recent Activity': 'recent_activity',
        'Trending': 'popular'
      };
      
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch('/api/catalogue/hosts', {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          page: pageNum, 
          limit: 20, 
          search,
          sort: sortMap[sort] || 'alphabetical',
          seed: seedToUse,
          favoritesOnly,
          verifiedOnly 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store the seed from the response
        if (data.seed !== undefined) {
          setRandomSeed(data.seed);
        }
        
        if (pageNum === 1) {
          setHosts(data.hosts || []);
        } else {
          setHosts(prev => [...prev, ...(data.hosts || [])]);
        }
        
        setHasMore(data.hasMore || false);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching hosts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch the full geocoded host set for the map (all eligible hosts at once;
  // geocoding is cached server-side so this is cheap after the first load).
  const fetchMapHosts = async () => {
    setMapLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch('/api/catalogue/hosts', {
        method: 'POST',
        headers,
        body: JSON.stringify({ mapMode: true }),
      });
      if (response.ok) {
        const data = await response.json();
        setMapHosts(data.hosts || []);
      }
    } catch (error) {
      console.error('Error fetching host map:', error);
    } finally {
      setMapLoading(false);
    }
  };

  // Lazily load the map dataset the first time the user opens Map view.
  useEffect(() => {
    if (viewMode === 'map' && authorized === true && !mapFetchedRef.current) {
      mapFetchedRef.current = true;
      fetchMapHosts();
    }
  }, [viewMode, authorized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep ?view= in sync so the chosen view is shareable.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (viewMode === 'map') next.set('view', 'map');
    else next.delete('view');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle search
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
    fetchHosts(1, searchInput, sortBy);
  };
  
  // Handle sort change
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setPage(1);
    setRandomSeed(null); // Reset seed when sort changes
    fetchHosts(1, searchTerm, newSort);
  };

  // Handle Enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
    setRandomSeed(null); // Reset seed for new random order
    fetchHosts(1, '', sortBy);
  };

  // Check subscription access
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let email = params.get('email') || '';
    if (!email) {
      try { email = localStorage.getItem('postaway_email') || ''; } catch (_) {}
    }

    (async () => {
      try {
        const res = await fetch('/api/access/subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        const data = await res.json();

        // User must have an active subscription to access hosts page
        if (res.ok && data.ok && data.active) {
          setAuthorized(true);
          setMembershipTier(data.membershipTier || 'all_access');
        } else {
          // Redirect to home page if no active subscription
          navigate('/');
        }
      } catch (err) {
        // Redirect to home page on error
        navigate('/');
      }
    })();
  }, [navigate]);

  // Initial load
  useEffect(() => {
    if (authorized === true) {
      fetchHosts(1, '', sortBy);
    }
  }, [authorized]);

  // Realtime search with debouncing
  useEffect(() => {
    // Skip if not authorized or searchInput hasn't been initialized or is same as searchTerm
    if (authorized !== true || searchInput === searchTerm) return;
    
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
      fetchHosts(1, searchInput, sortBy);
    }, 400); // 400ms delay

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput, authorized]);
  
  // Handle sort change effect - skip initial mount
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (authorized === true) {
      setPage(1);
      setRandomSeed(null);
      fetchHosts(1, searchTerm, sortBy);
    }
  }, [sortBy]);

  // Re-fetch when favoritesOnly or verifiedOnly changes
  useEffect(() => {
    if (authorized === true) {
      setPage(1);
      setRandomSeed(null);
      fetchHosts(1, searchTerm, sortBy);
    }
  }, [favoritesOnly, verifiedOnly]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchHosts(nextPage, searchTerm, sortBy);
    }
  };

  // Infinite scroll: auto-load next page when sentinel enters viewport
  const loadMoreSentinelRef = useRef(null);
  useEffect(() => {
    const node = loadMoreSentinelRef.current;
    if (!node) return;
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          handleLoadMore();
        }
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, page, searchTerm, sortBy, hosts.length]);

  const handleCardClick = (host, imageUrl) => {
    const propertyDisplay = host.property_name || 
      `${host.location_city}${host.location_state ? ', ' + host.location_state : ''}` ||
      'Property';
    // Just use the property name, not the brand name
    const title = propertyDisplay;
    
    setSelectedHost({
      ...host,
      title,
      location: (() => {
        const parts = [];
        if (host.location_city) parts.push(host.location_city);
        if (host.location_state) parts.push(host.location_state);
        if (host.location_country && host.location_country !== 'United States') {
          parts.push(host.location_country);
        }
        return parts.join(', ') || '';
      })(),
      image: imageUrl,
      plan_id: host.plan_id,
      plan_name: host.plan_name,
      handle: host.handle,
      user_id: host.user_id,
      property_id: host.property_id,
      has_active_collab_post: host.has_active_collab_post,
      active_travel_post_id: host.active_travel_post_id
    });
    setModalOpen(true);
  };

  // Open the existing property modal from a map pin.
  const openHostModalFromPin = (host) => {
    handleCardClick(host, host.property_image_url);
  };

  // Deep-link: open a specific property modal from ?property_id= (used by
  // "new property" notifications) or ?hostId= (used by "new host joined"
  // notifications, resolving to the host's most recent property). Falls back to
  // a targeted single-record fetch when the property isn't on the loaded page.
  const handledHostDeepLinkRef = useRef(false);
  useEffect(() => {
    if (handledHostDeepLinkRef.current) return;
    if (authorized !== true) return;

    const propertyId = searchParams.get('property_id');
    const hostId = searchParams.get('hostId');
    if (!propertyId && !hostId) return;

    handledHostDeepLinkRef.current = true;

    const openHost = (host) => {
      if (!host) return;
      handleCardClick(host, host.property_image_url);
    };

    if (propertyId) {
      const existing = hosts.find((h) => String(h.property_id) === String(propertyId));
      if (existing) {
        openHost(existing);
        return;
      }
    }

    (async () => {
      try {
        const headers = { 'Content-Type': 'application/json' };
        const token = getAuthToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch('/api/catalogue/hosts', {
          method: 'POST',
          headers,
          body: JSON.stringify(propertyId ? { id: propertyId } : { hostId }),
        });
        if (response.ok) {
          const data = await response.json();
          openHost((data.hosts || [])[0]);
        }
      } catch (err) {
        console.error('Error opening host from deep link:', err);
      }
    })();
  }, [authorized, hosts, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFavoriteToggle = async (propertyId) => {
    const result = await toggleFavorite({ propertyId });
    if (result !== null) {
      setHosts(prev => prev.map(h => 
        h.property_id === propertyId ? { ...h, is_favorited: result } : h
      ));
      if (selectedHost && selectedHost.property_id === propertyId) {
        setSelectedHost(prev => ({ ...prev, is_favorited: result }));
      }
    }
  };

  const handleFavoritesFilterToggle = () => {
    setFavoritesOnly(prev => !prev);
  };

  const content = (
    <div className="hosts-page">
      <div className="hosts-container">
        <div className="hosts-header">
          <div className="hosts-title">
            Our Hosts
          </div>
          <div className="hosts-description">
            <p>
              Explore amazing properties and exciting destinations looking for content creator collaborations.
            </p>

            {MAP_ENABLED && (
              <div className="hosts-view-toggle" role="tablist" aria-label="Hosts view">
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === 'grid'}
                  className={`hosts-view-toggle-btn${viewMode === 'grid' ? ' active' : ''}`}
                  onClick={() => setViewMode('grid')}
                >
                  Grid
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === 'map'}
                  className={`hosts-view-toggle-btn${viewMode === 'map' ? ' active' : ''}`}
                  onClick={() => setViewMode('map')}
                >
                  Map
                </button>
              </div>
            )}

            {/* Search Bar and Sort Dropdown */}
            {viewMode === 'grid' && (
            <div className="hosts-search-container">
              <div className="hosts-search-wrapper">
                <input
                  type="text"
                  placeholder="Search hosts by name, handle (no @), or location..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="hosts-search-input"
                  style={{ paddingRight: searchInput ? '40px' : '16px' }}
                  onFocus={(e) => e.target.style.borderColor = '#141416'}
                  onBlur={(e) => e.target.style.borderColor = '#E6E8EC'}
                />
                {searchInput && (
                  <button
                    onClick={handleClearSearch}
                    className="hosts-search-clear"
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="hosts-sort-wrapper">
                <div className="hosts-sort-icon-button">
                  <Dropdown
                    className="hosts-sort-dropdown"
                    value={sortBy}
                    setValue={handleSortChange}
                    options={sortOptions}
                    iconOnly={true}
                  />
                </div>
                <FavoriteFilterButton
                  active={favoritesOnly}
                  onClick={handleFavoritesFilterToggle}
                />
                <Tippy content={collabEligibleOnly ? "Show all properties" : "Show only collaboration-eligible properties"} placement="top">
                  <button
                    type="button"
                    className={`hosts-collab-filter-btn${collabEligibleOnly ? ' active' : ''}`}
                    onClick={() => setCollabEligibleOnly(prev => !prev)}
                    aria-label="Filter collaboration eligible"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <line x1="19" y1="8" x2="19" y2="14"/>
                      <line x1="22" y1="11" x2="16" y2="11"/>
                    </svg>
                  </button>
                </Tippy>
                <Tippy content={verifiedOnly ? "Show all hosts" : "Show only verified members"} placement="top">
                  <button
                    type="button"
                    className={`hosts-verified-filter-btn${verifiedOnly ? ' active' : ''}`}
                    onClick={() => setVerifiedOnly(prev => !prev)}
                    aria-label="Filter verified members"
                  >
                    <StampMark size={22} />
                  </button>
                </Tippy>
              </div>
            </div>
            )}
            
            {viewMode === 'grid' && searchTerm && (
              <p className="hosts-search-info">
                Searching for "{searchTerm}"
              </p>
            )}
            
            {viewMode === 'grid' && total > 0 && (
              <p className="hosts-search-info">
                Showing {hosts.length} of {total} properties
              </p>
            )}
          </div>
          {viewMode === 'map' ? (
            <Suspense fallback={<div className="hosts-map-loading">Loading map…</div>}>
              {mapLoading && mapHosts.length === 0 ? (
                <div className="hosts-map-loading">Loading map…</div>
              ) : (
                <CollabBoardMap
                  hosts={mapHosts}
                  creators={[]}
                  onSelectHost={openHostModalFromPin}
                  onClose={() => setViewMode('grid')}
                  photosOnly
                  actionLabel="View profile"
                />
              )}
            </Suspense>
          ) : (
          <div className="hosts-grid-container">
            {(() => {
              const withImages = hosts.filter(host => host.property_image_url);
              const premierHosts = withImages.filter(host => host.membership_tier !== 'basic');
              const basicHosts = collabEligibleOnly ? [] : withImages.filter(host => host.membership_tier === 'basic');

              return (
                <>
                  {premierHosts.length > 0 && (
                    <>
                      <h3 className="hosts-section-title">Premier Properties</h3>
                      <div className="hosts-grid">
                        {premierHosts.map((host, index) => (
                          <HostCard 
                            key={host.property_id || `host-${index}`} 
                            host={host}
                            onClick={handleCardClick}
                            onFavoriteToggle={handleFavoriteToggle}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {basicHosts.length > 0 && (
                    <>
                      <h3 className="hosts-section-title hosts-section-title-basic">Basic Properties</h3>
                      <div className="hosts-grid">
                        {basicHosts.map((host, index) => (
                          <HostCard 
                            key={host.property_id || `basic-${index}`} 
                            host={host}
                            onClick={handleCardClick}
                            onFavoriteToggle={handleFavoriteToggle}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              );
            })()}
            {favoritesOnly && !loading && hosts.length === 0 && (
              <div className="hosts-favorites-empty">
                <p>You haven't favorited any properties yet.</p>
                <p>Tap the heart icon on any property to save it here.</p>
              </div>
            )}
            {verifiedOnly && !favoritesOnly && !loading && hosts.length === 0 && (
              <div className="hosts-favorites-empty">
                <p>No verified members match your search.</p>
                <p>Learn how to earn the verified stamp on the <a href="/get-verified">Get Verified</a> page.</p>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {viewMode === 'grid' && loading && (
        <div className="hosts-loading">
          <div>Loading...</div>
        </div>
      )}

      {viewMode === 'grid' && hasMore && hosts.length > 0 && (
        <div
          ref={loadMoreSentinelRef}
          className="hosts-load-more-sentinel"
          aria-hidden="true"
          style={{ height: '1px', width: '100%' }}
        />
      )}

      {viewMode === 'grid' && !hasMore && hosts.length > 0 && (
        <div className="hosts-end-message">
          <p>
            You've reached the end of our host catalogue
          </p>
        </div>
      )}

      <PropertyModal 
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedHost(null);
        }}
        property={selectedHost}
        onFavoriteToggle={handleFavoriteToggle}
        membershipTier={membershipTier}
      />
    </div>
  );

  // Show loading state while checking authorization
  if (authorized === null) {
    return (
      <>
        <InfoPage
          belowHeader={<div style={{ textAlign: 'center', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", color: '#0f084b' }}>Loading...</div>}
          disableStamps
          customMinHeight={600}
          showInlineLivePulse
        />
      </>
    );
  }

  return (
    <>
      <InfoPage below={content} disableStamps customMinHeight={800} showInlineLivePulse />
    </>
  );
};

export default Hosts;
